import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, ArrowRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'oc_announcement_dismissed_v1';

/**
 * Felső értesítő sáv — a fejléc fölött, minden nyilvános oldalon. A tartalmát
 * és az érvényességét az admin állítja (/admin/promo → Felső értesítő sáv),
 * így a lejárat után bármikor újra bekapcsolható. A bezárást böngészőnként
 * megjegyezzük (verzióra kötve: új szöveghez bumpold a verziót).
 */
const AnnouncementBar = () => {
  const [bar, setBar] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/announcement-bar/active`, { timeout: 5000 });
        if (cancelled || !data?.bar) return;
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed && dismissed === (data.bar.version || 'default')) return;
        setBar(data.bar);
        setVisible(true);
      } catch (_e) { /* offline → nincs sáv */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // A fixed fejlécet és az oldal tartalmát is lejjebb toljuk a sáv magasságával.
  useEffect(() => {
    if (!visible) return undefined;
    const apply = () => {
      const h = ref.current?.offsetHeight || 0;
      document.documentElement.style.setProperty('--ann-h', `${h}px`);
      document.body.style.paddingTop = `${h}px`;
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      document.documentElement.style.setProperty('--ann-h', '0px');
      document.body.style.paddingTop = '';
    };
  }, [visible, bar]);

  if (!visible || !bar) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, bar.version || 'default'); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      ref={ref}
      data-testid="announcement-bar"
      className="fixed top-0 left-0 right-0 z-[60] bg-yellow-500 text-gray-950"
    >
      <div className="container mx-auto px-4 sm:px-6 py-2 flex items-center gap-3">
        <div className="flex-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center sm:text-left">
          <span
            data-testid="announcement-bar-text"
            className="text-[12px] sm:text-[13px] font-bold leading-snug"
            style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}
          >
            {/* Mobilon (768 px alatt) a rövid változat, felette a teljes szöveg. */}
            <span className="md:hidden">{bar.text_mobile || bar.text}</span>
            <span className="hidden md:inline">{bar.text}</span>
          </span>
          {bar.cta_href && (
            <a
              href={bar.cta_href}
              data-testid="announcement-bar-cta"
              className="group inline-flex items-center gap-1 text-[12px] sm:text-[13px] font-black underline underline-offset-2 whitespace-nowrap hover:text-gray-800 transition-colors"
            >
              {(bar.cta_label || 'Megnézem').replace(/\s*→\s*$/, '')}
              <ArrowRight size={13} strokeWidth={3} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Értesítés bezárása"
          data-testid="announcement-bar-close"
          className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-950/10 transition-colors"
        >
          <X size={14} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBar;
